import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api';
import ModalAssinatura from './ModalAssinatura';
import Icon from './ui/Icon';
import {
  NAV_SECTIONS,
  filterNavForRole,
  isNavItemActive,
  sectionContainsPath,
} from '../navigation';
import SeletorFazenda from './SeletorFazenda';
import { ouvirFazenda } from '../sessao';
import useSouMaster from '../hooks/useSouMaster';

function varianteAssinatura(a) {
  if (a.status === 'VENCIDO') return 'perigo';
  if (a.status === 'TRIAL') return 'info';
  if (a.diasRestantes <= 5) return 'alerta';
  return 'ok';
}

function iconeAssinatura(status) {
  if (status === 'VENCIDO') return 'alerta';
  if (status === 'TRIAL') return 'presente';
  return 'ok';
}

/**
 * Sidebar fixa para desktop — visível apenas em telas ≥ 769px.
 * No mobile, o menu lateral (hamburger) + bottom nav cumprem esse papel.
 */
export default function Sidebar({ onLogout, pendentes }) {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem('role') === 'ADMIN');
  const [assinatura, setAssinatura] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [abertas, setAbertas] = useState({});
  const isMaster = useSouMaster();

  const sections = useMemo(
    () => filterNavForRole(NAV_SECTIONS, isAdmin, isMaster),
    [isAdmin, isMaster]
  );

  useEffect(() => {
    setAbertas((prev) => {
      const next = { ...prev };
      sections.forEach((s) => {
        if (sectionContainsPath(s, location.pathname)) next[s.id] = true;
      });
      return next;
    });
  }, [location.pathname, sections]);

  useEffect(() => {
    const cache = sessionStorage.getItem('assinatura_cache');
    if (cache) {
      try {
        const { data, ts } = JSON.parse(cache);
        if (Date.now() - ts < 86400000) { setAssinatura(data); return; }
      } catch {}
    }
    api.getAssinatura().then(data => {
      setAssinatura(data);
      sessionStorage.setItem('assinatura_cache', JSON.stringify({ data, ts: Date.now() }));
    }).catch(() => {});
  }, []);

  // O papel depende da fazenda ativa: recomputa quando o usuário troca.
  useEffect(() => ouvirFazenda(() => setIsAdmin(localStorage.getItem('role') === 'ADMIN')), []);

  const toggle = (id) => setAbertas((prev) => ({ ...prev, [id]: !prev[id] }));

  const statusConhecido = assinatura && ['TRIAL', 'ATIVO', 'VENCIDO'].includes(assinatura.status);

  return (
    <aside className="sidebar" aria-label="Menu principal">
      {modalAberto && assinatura && (
        <ModalAssinatura assinatura={assinatura} onClose={() => setModalAberto(false)} />
      )}

      <div className="sidebar__brand">
        <span className="marca-icone marca-icone--md sidebar__brand-icon" aria-hidden="true">
          <img src={process.env.PUBLIC_URL + '/icone-sem-fundo.png'} alt="" />
        </span>
        <div className="sidebar__brand-texto">
          <div className="sidebar__brand-name">AgriGest</div>
          {localStorage.getItem('usuario') && <div className="sidebar__brand-user">{localStorage.getItem('usuario')}</div>}
        </div>
      </div>

      {/* Abaixo do bloco da marca: ocupa a largura toda, então o nome da
          fazenda cabe sem apertar ao lado do ícone. */}
      <SeletorFazenda variante="sidebar" />


      {statusConhecido && (
        <button
          className={`sidebar__assinatura sidebar__assinatura--${varianteAssinatura(assinatura)}`}
          onClick={() => setModalAberto(true)}
        >
          <span className="sidebar__assinatura-icon">
            <Icon nome={iconeAssinatura(assinatura.status)} tamanho={16} />
          </span>
          <div className="sidebar__assinatura-info">
            <div className="sidebar__assinatura-titulo">
              {assinatura.status === 'TRIAL' && `Período gratuito`}
              {assinatura.status === 'ATIVO' && `Plano ${assinatura.plano || 'Ativo'}`}
              {assinatura.status === 'VENCIDO' && 'Assinatura vencida'}
            </div>
            <div className="sidebar__assinatura-detalhe">
              {assinatura.status === 'TRIAL' && `${assinatura.diasRestantes} dia(s) restante(s)`}
              {assinatura.status === 'ATIVO' && (assinatura.diasRestantes <= 5 ? `Vence em ${assinatura.diasRestantes} dia(s)` : 'Ativo')}
              {assinatura.status === 'VENCIDO' && 'Clique para pagar'}
            </div>
          </div>
        </button>
      )}

      <nav className="sidebar__nav">
        <Link
          to="/"
          className={`sidebar__link ${location.pathname === '/' ? 'sidebar__link--active' : ''}`}
        >
          <span className="sidebar__link-icon"><Icon nome="inicio" tamanho={18} /></span>
          <span className="sidebar__link-text">Início</span>
        </Link>

        {sections.map((section) => {
          const aberta = !!abertas[section.id];
          // Quando a seção tem Notificações e está fechada, o badge de
          // pendências sobe para o cabeçalho — senão a pessoa não veria o
          // aviso sem antes abrir "Conta".
          const temNotificacoes = section.items.some((i) => i.to === '/notificacoes');
          const badgeSecao = temNotificacoes && !aberta && pendentes > 0;
          return (
            <div key={section.id} className="sidebar__group">
              <button
                type="button"
                className={`sidebar__group-toggle ${aberta ? 'sidebar__group-toggle--open' : ''}`}
                onClick={() => toggle(section.id)}
                aria-expanded={aberta}
              >
                <span className="sidebar__group-label">
                  {section.label}
                  {badgeSecao && <span className="sidebar__badge">{pendentes}</span>}
                </span>
                <span aria-hidden="true">{aberta ? '▾' : '▸'}</span>
              </button>
              {aberta && section.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`sidebar__link sidebar__link--nested ${
                    isNavItemActive(item, location.pathname, location.search)
                      ? 'sidebar__link--active'
                      : ''
                  }`}
                >
                  <span className="sidebar__link-icon"><Icon nome={item.icon} tamanho={18} /></span>
                  <span className="sidebar__link-text">{item.label}</span>
                  {item.to === '/notificacoes' && pendentes > 0 && (
                    <span className="sidebar__badge">{pendentes}</span>
                  )}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="sidebar__footer">
        <a
          href="https://wa.me/5554999487383?text=Ol%C3%A1!%20Gostaria%20de%20falar%20sobre%20o%20AgriGest"
          target="_blank"
          rel="noopener noreferrer"
          className="sidebar__link"
        >
          <span className="sidebar__link-icon"><Icon nome="suporte" tamanho={18} /></span>
          <span className="sidebar__link-text">Suporte</span>
        </a>
        <button className="sidebar__link sidebar__logout" onClick={onLogout}>
          <span className="sidebar__link-icon"><Icon nome="sair" tamanho={18} /></span>
          <span className="sidebar__link-text">Sair</span>
        </button>
      </div>
    </aside>
  );
}
